/**
 * Core type definitions for the TickerChart library.
 * Single source of truth for all types.
 * These types are shared across core modules, React, and React Native implementations.
 */

type ChartDateRange =
  | "1D"
  | "3D"
  | "5D"
  | "1W"
  | "1M"
  | "6M"
  | "YTD"
  | "1Y"
  | "5Y";

export type IndicatorId = "SMAVG" | "EMAVG" | "RSI" | "MACD" | "BOLL" | "WLPR";

// ==================== Data Points ====================

/**
 * Simple price point for line charts.
 */
export interface PricePoint {
  /** Timestamp in milliseconds since epoch */
  timestamp: number;
  /** Price value */
  value: number;
  /** Optional trading volume */
  volume?: number;
}

/**
 * OHLCV point for candlestick and OHLC charts.
 */
export interface OhlcvPoint {
  /** Timestamp in milliseconds since epoch */
  timestamp: number;
  /** Opening price */
  open: number;
  /** Highest price during period */
  high: number;
  /** Lowest price during period */
  low: number;
  /** Closing price */
  close: number;
  /** Optional trading volume */
  volume?: number;
}

// ==================== Series ====================

/**
 * Ticker series data with metadata.
 */
export interface TickerSeries {
  /** Unique identifier for the series */
  id: string;
  /** Display name (e.g., "AAPL") */
  name: string;
  /** Full name (e.g., "Apple Inc.") */
  longName?: string;
  /** Hex color for the series (auto-assigned if omitted) */
  color?: string;
  /** ISO currency code (e.g., "USD", "EUR", "INR") */
  issuedCurrency: string;
  /** ISO country code */
  countryCode?: string;
  /** Exchange identifier */
  exchangeCode?: string;
  /** Market status */
  marketStatus?: string;
  /** Previous closing price (renders as dashed horizontal line) */
  previousClose?: number;
  /** Time-series price data */
  priceMovements: PricePoint[] | OhlcvPoint[];
  extendedSessions?: {
    start: number;
    end: number;
  }[];
}

/**
 * Chart theme for light/dark mode support.
 * Consumer apps control which theme to apply.
 */
export type ChartTheme = "light" | "dark";

/**
 * Chart display mode.
 */
export type ChartMode = "single" | "comparison";

/**
 * Chart visualization type.
 */
export type ChartType = "line" | "candlestick";

/**
 * Comparison value display mode.
 * - 'absolute': Show raw price values
 * - 'normalized': Normalize to index 100 (value / base * 100)
 * - 'percentage': Show percentage change from baseline ((value - base) / base * 100)
 */
export type ComparisonValueMode = "absolute" | "normalized" | "percentage";

/**
 * Y-axis position.
 */
export type YAxisPosition = "left" | "right";

/**
 * Time range for the chart.
 */
export interface ChartTimeRange {
  /** Start timestamp in milliseconds */
  start: number;
  /** End timestamp in milliseconds */
  end: number;
}

/**
 * Volume sub-chart configuration.
 */
export interface ChartVolumeConfig {
  /** Enable volume chart */
  enabled: boolean;
  /** Height ratio (0.0 - 1.0), default 0.2 (20% of chart height) */
  heightRatio?: number;
}

/**
 * Comparison mode configuration.
 */
export interface ComparisonConfig {
  /** Value display mode, default: 'normalized' */
  valueMode?: ComparisonValueMode;
  /**
   * Normalization base point:
   * - 'first-visible': First visible data point
   * - 'first-data': First data point in the series
   * - 'previous-close': Use previousClose from series (falls back to first-data)
   * - number: Explicit base value
   */
  normalizationBase?:
    | "first-visible"
    | "first-data"
    | "previous-close"
    | number;
}

/**
 * Data interval for gap detection and time-based operations.
 * - Minutes: '1m', '5m', '15m', '30m'
 * - Hours: '1h', '2h', '4h'
 * - Days/Weeks: '1d', '1w'
 * - Months/Years: '1M', '1y'
 */
export type DataInterval =
  | "1m"
  | "5m"
  | "15m"
  | "30m"
  | "1h"
  | "2h"
  | "4h"
  | "1d"
  | "1w"
  | "1M"
  | "1y";

/**
 * Data normalization mode.
 * - 'none': Use absolute values
 * - 'percentage': % change from baseline
 * - 'indexed': Index to 100 at baseline
 */
export type DataNormalization = "none" | "percentage" | "indexed";

/**
 * Data processing configuration.
 * Handles normalization, gap detection/filling, downsampling, and time separators.
 */
export interface DataConfig {
  /** Required: Data interval for gap detection */
  interval: DataInterval;
  /** Fill interior gaps with LOCF (trailing gap always skipped). Default: false */
  fillGaps?: boolean;
  /** Normalization mode (default: 'none') */
  normalization?: DataNormalization;
  /** Auto-downsample using LTTB (~1 point per 2px, default: true) */
  autoDownsample?: boolean;
  /** Show time separator lines at day/week/month/year boundaries (default: true) */
  showTimeSeparators?: boolean;
}

/**
 * Processed data point with metadata for gap-filled points.
 */
export interface ProcessedPricePoint extends PricePoint {
  /** True if this point was forward-filled (not original data) */
  isFilled?: boolean;
}

/**
 * Gap region detected in time series data.
 */
export interface DataGap {
  /** Start timestamp of gap (last point before gap) */
  start: number;
  /** End timestamp of gap (first point after gap) */
  end: number;
  /** Gap size in milliseconds */
  size: number;
  /** True if this is a large gap (compressed in single mode, break in comparison) */
  isLarge: boolean;
}

/**
 * Interaction configuration.
 */
export interface ChartInteractionConfig {
  /** Enable zoom, default: true */
  zoomEnabled?: boolean;
  /** Enable pan, default: true */
  panEnabled?: boolean;
  /** Enable hover interactions, default: true */
  hoverEnabled?: boolean;
  /** Minimum visible time range in milliseconds */
  minZoomRange?: number;
  /** Maximum visible time range in milliseconds */
  maxZoomRange?: number;
}

/**
 * Crosshair configuration.
 */
export interface CrosshairConfig {
  /** Show vertical line, default: true */
  showVertical?: boolean;
  /** Show horizontal line, default: false */
  showHorizontal?: boolean;
}

/**
 * Chart padding configuration.
 */
export interface ChartPadding {
  /** Top padding, default: 20 */
  top?: number;
  /** Right padding, default: 60 (for y-axis) */
  right?: number;
  /** Bottom padding, default: 30 (for x-axis) */
  bottom?: number;
  /** Left padding, default: 10 */
  left?: number;
}

/**
 * Custom axis formatters.
 */
export interface ChartAxisFormatters {
  /** X-axis (time) formatter */
  xAxis?: (timestamp: number, isCrosshair?: boolean) => string;
  /** Y-axis (price) formatter */
  yAxis?: (value: number, currency?: string) => string;
}

/**
 * Hover event in single mode.
 */
export interface SingleHoverEvent {
  mode: "single";
  /** Screen X coordinate */
  screenX: number;
  /** Screen Y coordinate */
  screenY: number;
  /** X position within chart area */
  chartX: number;
  /** Y position within chart area */
  chartY: number;
  /** Timestamp snapped to nearest data point */
  timestamp: number;
  /** Series metadata */
  series: TickerSeries;
  /** Nearest data point (null if none) */
  datum: TickerDataPoint;
}

export type TickerDataPoint = PricePoint | OhlcvPoint | null;

/**
 * Hover event in comparison mode.
 */
export interface ComparisonHoverEvent {
  mode: "comparison";
  /** Screen X coordinate */
  screenX: number;
  /** Screen Y coordinate */
  screenY: number;
  /** X position within chart area */
  chartX: number;
  /** Y position within chart area */
  chartY: number;
  /** Timestamp snapped to nearest data point */
  timestamp: number;
  /** Map of seriesId to nearest point */
  seriesPoints: Record<string, TickerDataPoint>;
  /** Map of seriesId to series metadata */
  seriesMeta: Record<string, TickerSeries>;
}

/**
 * Hover event (discriminated union).
 */
export type HoverEvent = SingleHoverEvent | ComparisonHoverEvent;

/**
 * Click event in single mode.
 */
export type SingleClickEvent = SingleHoverEvent;

/**
 * Click event in comparison mode.
 */
export type ComparisonClickEvent = ComparisonHoverEvent;

/**
 * Click event (discriminated union).
 */
export type ClickEvent = SingleClickEvent | ComparisonClickEvent;

/**
 * Viewport change event.
 */
export interface ViewportChangeEvent {
  /** Chart mode */
  mode: ChartMode;
  /** New visible range */
  visibleRange: ChartTimeRange;
  /** What triggered the change */
  trigger: "pan" | "zoom" | "api" | "autoScroll";
}

/**
 * Imperative API for real-time updates and interactions.
 */
export interface TickerChartApi {
  /**
   * Append new data points to a series.
   * Use for real-time updates. Triggers imperative redraw, no React re-render.
   */
  appendData(
    seriesId: string,
    points: Array<PricePoint | OhlcvPoint>,
    messageType?: MarketBarMessageType
  ): void;

  /**
   * Replace all data for a series.
   * Use sparingly; prefer appendData for real-time.
   */
  setSeriesData(seriesId: string, data: Array<PricePoint | OhlcvPoint>): void;

  /**
   * Add a new series (comparison mode only).
   */
  addSeries(series: TickerSeries): void;

  /**
   * Remove a series by ID.
   */
  removeSeries(seriesId: string): void;

  /**
   * Set the visible time range.
   */
  setVisibleRange(range: ChartTimeRange): void;

  /**
   * Reset to show all data.
   */
  resetView(): void;

  /**
   * Zoom in by factor (e.g., 1.5 = 50% closer).
   */
  zoomIn(factor?: number): void;

  /**
   * Zoom out by factor.
   */
  zoomOut(factor?: number): void;

  /**
   * Pan by time delta (positive = forward).
   */
  panBy(deltaMs: number): void;

  /**
   * Get current series data.
   */
  getSeries(): TickerSeries[];

  /**
   * Get current visible range.
   */
  getVisibleRange(): ChartTimeRange;
}

/**
 * Auto-scroll behavior when new realtime data arrives.
 * - 'scroll': Shift viewport to follow latest data (keeps visible range constant)
 * - 'grow': Extend viewport end to include new data (grows visible range)
 */
export type AutoScrollBehavior = "scroll" | "grow";

export type MarketBarMessageType =
  | "MarketBarStart"
  | "MarketBarUpdate"
  | "MarketBarEnd"
  | "MarketBarIntervalEnd";

export type TickerChartDateRange = ChartDateRange | "1H";

/**
 * Real-time configuration.
 */
export interface RealtimeConfig {
  /** Auto-scroll to follow latest data when at right edge */
  autoScroll?: boolean;
  /**
   * Auto-scroll behavior (default: 'grow')
   * - 'scroll': Shift viewport to follow latest data (constant visible range)
   * - 'grow': Extend viewport end to include new data (expanding visible range)
   */
  autoScrollBehavior?: AutoScrollBehavior;
  /** Trailing buffer ratio for auto-grow mode (default: 0.1 = 10%) */
  bufferRatio?: number;
  /** Cap history per series (default: 5000) */
  maxPoints?: number;
  /** Redraw throttle in milliseconds (default: 16 for 60fps) */
  throttleMs?: number;
  /** Show pulsing dot at last data point (default: true when autoScroll is enabled) */
  showRealtimeDot?: boolean;
  /** Interval duration in ms for auto-scroll threshold calculation */
  intervalMs?: number;
}

/**
 * Base props shared by all chart modes.
 */
export interface TickerChartBaseProps {
  /** Chart height in pixels */
  height: number;
  /** Chart width in pixels or 'auto' to use container width */
  width?: number | "auto";
  /** Chart padding */
  padding?: ChartPadding;
  /** Initial visible time range */
  initialRange?: ChartTimeRange;
  /** Interaction configuration */
  interactions?: ChartInteractionConfig;
  /** Crosshair configuration */
  crosshair?: CrosshairConfig;
  /** Y-axis position, default: 'right' */
  yAxisPosition?: YAxisPosition;
  /** Show gradient fill under line chart, default: true */
  showGradient?: boolean;
  /** Custom axis formatters */
  formatters?: ChartAxisFormatters;
  /** Hover event handler */
  onHover?: (event: HoverEvent) => void;
  /** Click event handler */
  onClick?: (event: ClickEvent) => void;
  /** Viewport change event handler */
  onViewportChange?: (event: ViewportChangeEvent) => void;
  /**
   * Callback to render custom tooltip content.
   * Receives the hover event and should return a React node.
   * Tooltip is only rendered if this callback is provided and returns a non-null value.
   * Return null or undefined to hide the tooltip.
   */
  renderTooltip?: (event: HoverEvent) => React.ReactNode;
  /** Real-time update configuration */
  realtime?: RealtimeConfig;
  /** Data processing configuration (normalization, gaps, downsampling) */
  dataConfig?: DataConfig;
  /** ARIA label for the chart */
  ariaLabel?: string;
  /** ARIA description for the chart */
  ariaDescription?: string;
  /** Called when the API is ready */
  onReady?: (api: TickerChartApi) => void;
  /** Number of ticks on the X-axis, default: 4 */
  xAxisTickSize?: number;
  /** Number of ticks on the Y-axis, default: 3 */
  yAxisTickSize?: number;
  /** Enable debug mode */
  debug?: boolean;
  /** Chart theme (light or dark), default: 'light' */
  theme?: ChartTheme;
  /**
   * Viewport end buffer configuration.
   * Adds trailing space after the last data point.
   * - false: No buffer (data fills to edge)
   * - number: Buffer ratio (default: 0.1 = 10% of data range)
   */
  viewportEndBuffer?: number | false;
  /** Animate chart reveal (left-to-right SVG clipPath transition) on initial load */
  animate?: boolean;
  /** Duration of reveal animation in milliseconds (default: 800) */
  animationDuration?: number;
  /** Use cumulative timeline for x-axis ticks (collapses overnight/weekend gaps) */
  intraday?: boolean;
  /** Base font size for typography scaling (px). When provided, replaces the default 14px base. Responsive scaling and clamping (10-16px) still apply. */
  baseFontSize?: number;
  /** After-hours time period overlays. Each period renders a semi-transparent rectangle. */
  afterHoursPeriods?: AfterHoursTimePeriod | AfterHoursTimePeriod[];
  /** Dynamic content from consumer */
  children?: React.ReactNode;
}

/**
 * Props for single ticker mode.
 */
export interface TickerChartSingleProps extends TickerChartBaseProps {
  mode: "single";
  /** Chart type, default: 'line' */
  chartType?: ChartType;
  /** Volume sub-chart configuration */
  volume?: ChartVolumeConfig;
  /** Single series (can be wrapped in array) */
  series: TickerSeries | [TickerSeries];
  /** Show baseline reference line and expand Y-domain to include baseline value (default: false) */
  showBaseline?: boolean;
  /** Enable split-color (green/red) line rendering above/below baseline (default: false) */
  showMultiColorLine?: boolean;
  /** Use previousClose as baseline for color/split decisions (default: false).
   *  When false, the first visible data point is used as baseline. */
  usePreviousCloseAsBaseline?: boolean;
  /** Show last visible price indicator (dot, line, and Y-axis label), default: false */
  showLastVisiblePrice?: boolean;
}

/**
 * Props for comparison mode.
 */
export interface TickerChartComparisonProps extends TickerChartBaseProps {
  mode: "comparison";
  /** Chart type (only 'line' supported) */
  chartType?: "line";
  /** Comparison configuration */
  comparison?: ComparisonConfig;
  /** Volume not supported in comparison mode */
  volume?: never;
  /** Multiple series (2+ required) */
  series: [TickerSeries, TickerSeries, ...TickerSeries[]];
  /** Show last visible price indicator (dot, line, and Y-axis label), default: false */
  showLastVisiblePrice?: boolean;
}

/**
 * Chart props (discriminated union).
 */
export type TickerChartProps =
  | TickerChartSingleProps
  | TickerChartComparisonProps;

// ==================== Internal Types ====================

/**
 * Scale interface for mapping values to pixels and vice versa.
 */
export interface Scale {
  /** Get the domain [min, max] */
  domain: () => [number, number];
  /** Get the range [min, max] in pixels */
  range: () => [number, number];
  /** Map domain value to pixel position */
  scale: (value: number) => number;
  /** Map pixel position back to domain value */
  invert: (pixel: number) => number;
}

/**
 * Typography settings for chart labels.
 * Font sizes scale based on chart dimensions.
 */
export interface ChartTypography {
  /** Font size for axis tick labels (px) */
  axisLabelSize: number;
  /** Font size for secondary axis labels like dates (px) */
  secondaryLabelSize: number;
  /** Approximate character width for label calculations (px) */
  charWidth: number;
  /** Line height multiplier */
  lineHeight: number;
}

/**
 * Chart layout regions.
 */
export interface ChartLayout {
  /** Container width */
  width: number;
  /** Container height */
  height: number;
  /** Price chart area */
  priceArea: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  /** Volume chart area (null if disabled) */
  volumeArea: {
    x: number;
    y: number;
    width: number;
    height: number;
  } | null;
  /** X-axis area */
  xAxisArea: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  /** Y-axis area */
  yAxisArea: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  /** Width of the actual chart (excluding axes) */
  chartWidth: number;
  /** Total height of price + volume areas */
  chartHeight: number;
  /** Typography settings scaled to chart dimensions */
  typography: ChartTypography;
}

/**
 * Hover state for internal use.
 * Used by ChartStore for tracking cursor position.
 */
export interface HoverState {
  /** Whether hover is active */
  visible: boolean;
  /** Screen X coordinate */
  screenX: number;
  /** Screen Y coordinate */
  screenY: number;
  /** Chart X coordinate */
  chartX: number;
  /** Chart Y coordinate */
  chartY: number;
  /** Timestamp snapped to nearest data (null if no data) */
  timestamp: number | null;
  /** Map of seriesId to nearest point */
  nearestPoints: Map<string, TickerDataPoint>;
}

/**
 * Store event types for subscriptions.
 */
export type ChartStoreEvent =
  | "data"
  | "viewport"
  | "scales"
  | "hover"
  | "layout"
  | "config";

/**
 * Configuration for layout calculation.
 */
export interface LayoutConfig {
  /** Container width in pixels */
  width: number;
  /** Container height in pixels */
  height: number;
  /** Chart padding */
  padding?: ChartPadding;
  /** Volume sub-chart configuration */
  volume?: ChartVolumeConfig;
  /** Y-axis position, default: 'right' */
  yAxisPosition?: YAxisPosition;
  /** X-axis height in pixels, default: 30 */
  xAxisHeight?: number;
  /** Y-axis width in pixels, default: 60 */
  yAxisWidth?: number;
  /** Base font size for typography scaling (px), default: 14 */
  baseFontSize?: number;
}

/**
 * Validation result for layout and data validation.
 */
export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings?: string[];
}

/**
 * Chart area types for hit detection.
 */
export type ChartArea =
  | "price"
  | "volume"
  | "xAxis"
  | "yAxis"
  | "padding"
  | "outside";

/**
 * Viewport representing the visible time range.
 */
export interface Viewport {
  start: number;
  end: number;
}

/**
 * Time separator type for visual guides.
 */
export type TimeSeparatorType = "hour" | "day" | "week" | "month" | "year";

/**
 * Time separator for visual guides.
 */
export interface TimeSeparator {
  timestamp: number;
  type: TimeSeparatorType;
  label: string;
}

export interface AfterHoursTimePeriod {
  readonly start: number;
  readonly end: number;
  readonly color?: string;
  readonly opacity?: number;
}

/**
 * Internal state for the ChartStore.
 */
export interface ChartStoreState {
  mode: ChartMode;
  chartType: ChartType;
  theme: ChartTheme;
  comparisonConfig?: ComparisonConfig;
  dataConfig?: DataConfig;
  /** Whether intraday cumulative timeline ticks are active (default: false) */
  intraday: boolean;
  /** Viewport end buffer ratio (0 = disabled) */
  viewportEndBuffer: number;

  seriesData: Map<string, (PricePoint | OhlcvPoint)[]>;
  seriesMeta: Map<string, TickerSeries>;

  /** Processed data after gap filling, normalization, and downsampling */
  processedData: Map<string, ProcessedPricePoint[]>;
  /** Time separators for visual guides */
  timeSeparators: TimeSeparator[];

  layout: ChartLayout;
  viewport: Viewport;
  xScale: Scale;
  yScale: Scale;
  volumeScale: Scale | null;
  yDomain: [number, number];
  volumeDomain: [number, number] | null;
  /** Base value for direction-based color decisions (previousClose or first visible point) */
  baseValue: number;

  hover: HoverState;
  isDragging: boolean;
  isPinching: boolean;
}

/**
 * Listener callback for store events.
 */
export type ChartStoreListener = () => void;

/**
 * Configuration for creating a ChartStore.
 */
export interface ChartStoreConfig {
  mode: ChartMode;
  chartType: ChartType;
  theme?: ChartTheme;
  series: TickerSeries[];
  width: number;
  height: number;
  padding?: ChartPadding;
  initialViewport?: { start: number; end: number };
  volumeEnabled?: boolean;
  volumeHeightRatio?: number;
  yAxisPosition?: YAxisPosition;
  comparisonConfig?: ComparisonConfig;
  dataConfig?: DataConfig;
  /** Whether chart shows intraday data with overnight gaps (uses cumulative timeline ticks) */
  intraday?: boolean;
  realtime?: RealtimeConfig;
  /** Viewport end buffer ratio (default: 0.1, false to disable) */
  viewportEndBuffer?: number | false;
  /** Whether to use previousClose as the y-axis baseline (affects y-domain computation) */
  usePreviousCloseAsBaseline?: boolean;
  /** Base font size for typography scaling (px), default: 14 */
  baseFontSize?: number;
}

/**
 * ChartStore interface for state management.
 */
export interface ChartStore {
  getState(): ChartStoreState;

  getSeriesData(seriesId: string): (PricePoint | OhlcvPoint)[];

  getVisibleData(seriesId: string): (PricePoint | OhlcvPoint)[];

  getRenderableData(seriesId: string): (PricePoint | OhlcvPoint)[];

  getHoverState(): HoverState;

  getViewport(): Viewport;

  getScales(): { x: Scale; y: Scale; volume: Scale | null };

  getLayout(): ChartLayout;

  getTimeSeparators(): TimeSeparator[];

  subscribe(event: ChartStoreEvent, listener: ChartStoreListener): () => void;

  subscribeMany(
    events: ChartStoreEvent[],
    listener: ChartStoreListener
  ): () => void;

  appendData(
    seriesId: string,
    points: (PricePoint | OhlcvPoint)[],
    messageType?: MarketBarMessageType
  ): void;

  setSeriesData(seriesId: string, data: (PricePoint | OhlcvPoint)[]): void;

  addSeries(series: TickerSeries): void;

  removeSeries(seriesId: string): void;

  setViewport(viewport: Viewport): void;

  /**
   * Set viewport without snapping to data points.
   * Use during drag operations for smooth movement.
   * Call snapViewport() after drag ends to align with data.
   */
  setViewportWithoutSnap(viewport: Viewport): void;

  pan(deltaX: number): void;

  /**
   * Pan without snapping to data points.
   * Use during drag operations for smooth movement.
   * Call snapViewport() after drag ends to align with data.
   */
  panWithoutSnap(deltaX: number): void;

  /**
   * Snap viewport boundaries to nearest data points.
   * Call after pan/zoom operations complete (e.g., on mouse up).
   */
  snapViewport(): void;

  zoom(factor: number, anchorX: number): void;

  resetView(): void;

  setHover(state: Partial<HoverState>): void;

  clearHover(): void;

  setDimensions(width: number, height: number, baseFontSize?: number): void;

  setDragging(isDragging: boolean): void;

  setPinching(isPinching: boolean): void;

  destroy(): void;
}

/**
 * Check if a point is a PricePoint.
 */
export function isPricePoint(
  point: PricePoint | OhlcvPoint
): point is PricePoint {
  return "value" in point && !("open" in point);
}

/**
 * Check if a point is an OhlcvPoint.
 */
export function isOhlcvPoint(
  point: PricePoint | OhlcvPoint
): point is OhlcvPoint {
  return (
    "open" in point && "high" in point && "low" in point && "close" in point
  );
}

/**
 * Check if props are for single mode.
 */
export function isSingleMode(
  props: TickerChartProps
): props is TickerChartSingleProps {
  return props.mode === "single";
}

/**
 * Check if props are for comparison mode.
 */
export function isComparisonMode(
  props: TickerChartProps
): props is TickerChartComparisonProps {
  return props.mode === "comparison";
}
